import FeaturesMap from '@apps-common/ui-maps/FeaturesMap.jsx';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {APPROVAL_OUTCOMES, FEATURE_STATE} from '@app/constants/uploads';
import {GeojsonLayer} from '@apps-common/ui-maps';
import {
  useApproveFeature,
  useGetUploadReviewSingleFeature,
} from '@api/asyncIngest.js';
import {useTheme} from '@mui/material/styles/index.js';

const ChipAndLabel = ({color, label}) => (
  <Box sx={{display: 'flex', alignItems: 'center'}}>
    <Box
      sx={{
        backgroundColor: `${color}.light`,
        border: 'solid 2px',
        borderColor: `${color}.main`,
        display: 'inline-block',
        height: '1em',
        width: '1em',
        marginRight: 0.5,
      }}
    />
    <Typography component="span" variant="subtitle2">
      {label}
    </Typography>
  </Box>
);

ChipAndLabel.propTypes = {
  color: PropTypes.string,
  label: PropTypes.node,
};

const ReviewSingleFeature = ({upload, features}) => {
  const theme = useTheme();

  const [currentFeature, setCurrentFeature] = useState(0);

  const feature = useMemo(() => {
    return features[currentFeature];
  }, [features, currentFeature]);

  const disablePrev = useMemo(() => currentFeature <= 0, [currentFeature]);
  const disableNext = useMemo(
    () => currentFeature >= features.length - 1,
    [currentFeature, features]
  );

  // move to the next feature, without going out of bounds
  const nextFeature = () => {
    if (currentFeature < features.length - 1) {
      setCurrentFeature(currentFeature + 1);
    }
  };

  // move to the previous feature, without going below 0.
  const prevFeature = () => {
    if (currentFeature > 0) {
      setCurrentFeature(currentFeature - 1);
    }
  };

  const {
    data: featureData,
    error,
    isPending,
  } = useGetUploadReviewSingleFeature({upload, feature});

  // a "feature" in the context of this component is not a GeoJSON feature,
  // but metadata about a feature in the API. Since we need a GeoJSON feature
  // to use with the map, we need to create one using either the original or
  // alternate geometry.
  const geojsonFeature = feat => {
    return {
      type: 'Feature',
      geometry: feat,
    };
  };

  const featureLayers = useMemo(() => {
    const layers = {
      original: [],
      alternate: [],
    };

    if (isPending) {
      return layers;
    }

    if (featureData?.geometry) {
      layers.original.push(geojsonFeature(featureData.geometry));
    }

    if (featureData?.alternative) {
      layers.alternate.push(geojsonFeature(featureData.alternative));
    }

    return layers;
  }, [featureData, isPending]);

  const {mutate: approve, approveIsPending} = useApproveFeature(
    {upload, feature},
    {onSuccess: nextFeature}
  );

  const renderFeatureInfo = () => {
    if (isPending) {
      return <Typography>Loading...</Typography>;
    }

    if (error) {
      return <Typography>Error: {error.message}</Typography>;
    }

    if (!featureData) {
      return <Typography>No feature data</Typography>;
    }

    return (
      <Paper elevation={0} sx={{height: '100%', ml: 1}}>
        {featureData.state === FEATURE_STATE.ERROR && (
          <Card sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <CardContent>
              <ChipAndLabel color="primary" label="Feature (error)" />
              <Alert severity="error" variant="outlined" sx={{py: 0.2, my: 1}}>
                <Typography variant="caption">
                  This feature could not be imported due to an error. Please
                  check the geometry for validity. This feature will be skipped
                  if you proceed with the import.
                </Typography>
                {featureData.errors &&
                  featureData.errors.map(e => (
                    <Typography key={e} variant="caption">
                      {e}
                    </Typography>
                  ))}
              </Alert>
            </CardContent>
            <CardActions sx={{mt: 'auto'}}>
              <Button
                onClick={nextFeature}
                color="primary"
                variant="outlined"
                sx={{p: 0.5, ml: 'auto'}}
              >
                Skip
              </Button>
            </CardActions>
          </Card>
        )}

        {featureData.state !== FEATURE_STATE.ERROR && (
          <Stack
            gap={1}
            sx={{height: '100%', flex: 1, flexDirection: 'column'}}
          >
            <Card>
              <CardContent>
                <ChipAndLabel color="primary" label="Original geometry" />
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{py: 0.2, my: 1}}
                >
                  {featureData.errors ? (
                    featureData.errors.map(e => (
                      <Typography key={e} variant="caption">
                        {e}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="caption">
                      This feature has too many vertices and must be simplified.
                    </Typography>
                  )}
                </Alert>
                {featureData.area && (
                  <Typography variant="caption">
                    Original area: featureData.area km²
                  </Typography>
                )}
              </CardContent>
            </Card>
            <Card sx={{flex: 1, flexDirection: 'column'}}>
              <CardContent sx={{flex: 1, flexDirection: 'column'}}>
                <ChipAndLabel color="secondary" label="Simplified geometry" />
                <Box sx={{flex: 1}}>
                  {featureData.alt_area && (
                    <Typography variant="caption">
                      New area: {featureData.area} km²
                    </Typography>
                  )}
                  {featureData.state === FEATURE_STATE.APPROVED && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{py: 0.2, my: 1}}
                    >
                      Simplification accepted. This feature will be imported.
                    </Alert>
                  )}
                  {featureData.state === FEATURE_STATE.DISCARDED && (
                    <Alert
                      severity="error"
                      variant="outlined"
                      sx={{py: 0.2, my: 1}}
                    >
                      Simplification rejected. This feature will be skipped.
                    </Alert>
                  )}
                </Box>
              </CardContent>
              <CardActions
                sx={{
                  display: 'flex',
                  flexGrow: 0,
                  justifyContent: 'right',
                  mt: 'auto',
                }}
              >
                <Button
                  onClick={() => approve(APPROVAL_OUTCOMES.ACCEPT)}
                  color="primary"
                  variant="outlined"
                  disabled={
                    approveIsPending ||
                    featureData.state === FEATURE_STATE.APPROVED
                  }
                  sx={{p: 0.5}}
                >
                  Approve
                </Button>
                <Button
                  color="error"
                  onClick={() => approve(APPROVAL_OUTCOMES.DISCARD)}
                  disabled={
                    approveIsPending ||
                    featureData.state === FEATURE_STATE.DISCARDED
                  }
                  variant="outlined"
                  sx={{p: 0.5}}
                >
                  Reject
                </Button>
              </CardActions>
            </Card>
          </Stack>
        )}
      </Paper>
    );
  };

  return (
    <>
      {features?.length > 0 && (
        <Stack
          direction="row"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            onClick={prevFeature}
            disabled={disablePrev}
            sx={{ml: 'auto'}}
          >
            Previous
          </Button>
          <Typography>
            {currentFeature + 1} of {features.length}
          </Typography>
          <Button
            onClick={nextFeature}
            disabled={disableNext}
            sx={{mr: 'auto'}}
          >
            Next
          </Button>
        </Stack>
      )}
      <Grid container>
        <Grid item xs={12} lg={6}>
          <Box sx={{minHeight: 300, height: '100%', width: '100%'}}>
            <FeaturesMap
              features={featureLayers.original}
              animationDuration={0}
            >
              <GeojsonLayer
                features={featureLayers.alternate}
                style={[
                  {
                    'stroke-width': 5,
                    'stroke-color': theme.palette.secondary.light,
                  },
                  {
                    'stroke-width': 3,
                    'stroke-color': theme.palette.secondary.main,
                  },
                ]}
              />
            </FeaturesMap>
          </Box>
        </Grid>
        <Grid item xs={12} lg={6}>
          {renderFeatureInfo()}
        </Grid>
      </Grid>
    </>
  );
};

ReviewSingleFeature.propTypes = {
  upload: PropTypes.object,
  features: PropTypes.arrayOf(PropTypes.object),
};

export default ReviewSingleFeature;
